<template>
  <div>
    <v-dialog
      v-model="dialog"
      width="600"
      transition="dialog-transition"
    >
      <v-sheet>
        <v-toolbar dark class="primary">
          <v-btn
            icon
            @click="onClose"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-toolbar-title>Transferencia de subsidio</v-toolbar-title>
        </v-toolbar>
        <v-container>
          <cuentas-selected v-if="cuentaDestino && cuentaDestino.id"></cuentas-selected>
          <cuentas-select v-else v-model="cuentaDestinoId"></cuentas-select>
          <v-text-field
            v-if="importeMaximo != null"
            :value="importeMaximo | formatToTwoDecimals"
            readonly
            label="Subsidio a favor (expresado en pesos)"
            prepend-icon="mdi-currency-usd"
            color="primary"
          ></v-text-field>
          <v-text-field
            v-else
            value="0"
            readonly
            label="Subsidio a favor (expresado en pesos)"
            prepend-icon="mdi-currency-usd"
            color="primary"
          ></v-text-field>
          <importe-input
            :value="importe"
            :rules="rules.importe"
            @input="onInput"
            @enter="onSubmit"
          ></importe-input>
          <v-text-field
            v-model="cantidad"
            label="Cantidad a favor"
            prepend-icon="mdi-weight-kilogram"
            color="primary"
            type="number"
            :rules="rules.cantidad"
            @keydown.enter="onSubmit"
            @input="handleInputCantidad"
          ></v-text-field>
          <v-text-field
            v-model="detalle"
            label="Detalle"
            prepend-icon="mdi-text"
            color="primary"
            :rules="rules.detalle"
            @keydown.enter="onSubmit"
          ></v-text-field>
          <v-footer>
            <v-btn
              :disabled="!updatable && !$can('transacciones.transferencias')"
              color="primary"
              @click="onSubmit"
              :loading="loading"
              block
            >
              Confirmar
            </v-btn>
          </v-footer>
        </v-container>
      </v-sheet>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex'
import CuentaSelect from '@/pages/transferencias/SelectComponent'
import CuentaSelected from '@/pages/transferencias/SelectedComponent'
import PrecioMixin from '@/mixins/Precios'
import ImporteInput from '@/components/ImporteInput'
export default {
  name: 'TransferenciaComponent',
  mixins: [PrecioMixin],
  components: {
    'importe-input': ImporteInput,
    'cuentas-select': CuentaSelect,
    'cuentas-selected': CuentaSelected
  },
  model: {
    prop: 'selected',
    event: 'change'
  },
  props: {
    selected: Boolean
  },
  data () {
    return {
      hoy: null,
      isSubmitting: false,
    }
  },
  beforeDestroy () {
    // Remueve el event listener en el gancho beforeDestroy
    this.onLimpiar()
  },

  mounted () {
    this.cuentaOrigenId = this.selectedId
    this.hoy = this.$moment().format('YYYY-MM-DD')
  },
  computed: {
    ...mapGetters('Cuentas', ['selectedId']),
    ...mapGetters('Transferencias', { transferenciaEdited: 'edited', cuentaDestino: 'cuenta', transferenciaLoading: 'loading', rules: 'rules' }),
    dialog: {
      get () {
        return this.selected
      },
      set (value) {
        this.$emit('change', value)
      }
    },
    importeMaximo() {
      return this.$store.getters['Cuentas/selected'].saldo_subsidio
    },
    importe: {
      get () {
        return this.transferenciaEdited.importe
      },
      set (value) {
        this.setTransferenciaEdited({
          ...this.transferenciaEdited,
          importe: value,
        })
      }
    },
    cantidad: {
      get () {
        return this.transferenciaEdited.cantidad
      },
      set (value) {
        this.setTransferenciaEdited({
          ...this.transferenciaEdited,
          cantidad: value,
        })
      }
    },
    detalle: {
      get () {
        return this.transferenciaEdited.detalle
      },
      set (value) {
        this.setTransferenciaEdited({
          ...this.transferenciaEdited,
          detalle: value,
        })
      }
    },
    cuentaDestinoId: {
      get () {
        return this.transferenciaEdited.cuenta_destino_id
      },
      set (value) {
        this.setTransferenciaEdited({
          ...this.transferenciaEdited,
          cuenta_destino_id: value,
        })
      }
    },
    loading () {
      return this.transferenciaLoading
    },
    updatable () {
      // Validar que el importe cumpla con todas las reglas
      const importeValido = this.rules.importe.every(rule => rule(this.importe) === true)
      return importeValido && this.cuentaDestinoId > 0
    },
    cuentaOrigenId: {
      get () {
        return this.transferenciaEdited.cuenta_origen_id
      },
      set (value) {
        this.setTransferenciaEdited({
          ...this.transferenciaEdited,
          cuenta_origen_id: value,
        })
      }
    },
  },
  watch: {
    async dialog (newVal) {
      if (newVal) {
        this.onLimpiar();
      }
    },
    selectedId (newValue) {
      this.cuentaOrigenId = newValue
    },
    importe (newValue) {
      // Calcular cantidad automáticamente cuando cambia el importe
      if (newValue > 0) {
        const precio = this.precioByVigencia(this.hoy);
        const subsidio = precio && precio.subsidio ? precio.subsidio : 0;
        if (subsidio > 0) {
          this.cantidad = newValue / subsidio;
        }
      } else {
        this.cantidad = 0;
      }
    },
  },
  methods: {
    ...mapActions('Transferencias', { onSaveTransferencia: 'onSave', onClearEditedTransferencia: 'onClearEdited', onClearCuentaTransferencia: 'onClearCuenta' }),
    ...mapMutations('Transferencias', { setTransferenciaEdited: 'setEdited' }),
    async onLimpiar () {
      this.onClearEditedTransferencia()
      this.onClearCuentaTransferencia()
      this.cuentaOrigenId = await this.selectedId;
    },
    onSubmit(event) {
      event.preventDefault();
      // Si ya estamos enviando, salimos de la función
      if (this.isSubmitting) {
        return;
      }
      this.isSubmitting = true; // Establecer la bandera en true

      this.onSaveTransferencia().then(response => {
        this.onLimpiar();
        this.$store.dispatch('Transacciones/onFetch');
        this.$store.dispatch('Cuentas/onRefresh');
        this.dialog = false
      })
      .finally(() => {
        this.isSubmitting = false; // Restablecer la bandera a false al finalizar
      });
    },
    onClose (event) {
      event.preventDefault()
      this.dialog = false
    },
    onInput(newVal) {
      this.importe = newVal;
    },
    handleInputCantidad(newVal) {
      const cantidad = parseFloat(newVal);
      if (cantidad > 0) {
        const precio = this.precioByVigencia(this.hoy);
        const subsidio = precio && precio.subsidio ? precio.subsidio : 0;
        if (subsidio > 0) {
          this.importe = parseFloat(cantidad * subsidio);
        }
      }
    }
  },
}
</script>
